import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { saveAs } from 'file-saver';
import { AccessLevelEnum, ApplianceRole, AuthenticationService, CadenceRole } from 'src/app/services/authentication/authentication.service';
import { RfpService } from 'src/app/services/rfp/rfp.service';
import { Rfp } from 'src/app/domain/rfp.model';
import { SpinnerServiceService } from 'src/app/shared/services/spinner-service.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-rfp',
  templateUrl: './rfp.component.html',
  styleUrls: ['./rfp.component.scss'],
  providers: [MessageService]
})
export class RfpComponent implements OnInit {
  @ViewChild('dt1') table: Table;
  rfpData: Rfp[] = [];
  selectedRfpAuditHistory: any[] = [];
  displayAuditHistoryDialog: boolean = false;

  // roles
  applianceRole: ApplianceRole;
  cadenceRole: CadenceRole;
  public accessLevelEnum = AccessLevelEnum;

  constructor(
    private rfpService: RfpService,
    public authenticationService: AuthenticationService,
    private messageService: MessageService,
    private router: Router
  ) { }

  ngOnInit(): void {
    SpinnerServiceService.showSpinner();
    this.loadRfps();
    SpinnerServiceService.hideSpinner();
  }

  loadRfps(): void {
    this.rfpService.getRfps().subscribe({
      next: (data: Rfp[]) => {
        this.rfpData = data;
      },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load RFPs' });
      }
    });
  }

  navigateToAddRfp(): void {
    this.router.navigate(['/rfp/new']);
  }

  viewRfp(id: number): void {
    this.router.navigate(['/rfp/view', id], { state: { rfpId: id, mode: 'view' } });
  }

  editRfp(id: number): void {
    this.router.navigate(['/rfp/edit', id], { state: { rfpId: id, mode: 'edit' } });
  }

  deleteRfpById(id: number): void {
    this.rfpService.deleteRfp(id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'RFP deleted successfully' });
        this.loadRfps();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete RFP' });
      }
    });
  }

  exportExcel() {
    import("xlsx").then(xlsx => {
      let data: any[] = this.table.filteredValue ? this.table.filteredValue : this.table.value;

      if (!data || data.length === 0) {
        console.warn("No data available for export.");
        return;
      }

      let index = 0;

      const exportData = data.map((rfp: Rfp) => ({
        index: ++index,
        customerName: rfp.customerName,
        deploymentType: rfp.deploymentType,
        productGroup: rfp.productGroup,
        rfpReferenceNumber: rfp.rfpReferenceNumber,
        inquiryDate: rfp.inquiryDate ? new Date(rfp.inquiryDate).toISOString().split('T')[0] : 'N/A',
        submissionDate: rfp.submissionDate ? new Date(rfp.submissionDate).toISOString().split('T')[0] : 'N/A',
        projectDuration: rfp.projectDuration,
        location: rfp.location,
        initialBidBond: rfp.initialBidBond,
        finalBidBond: rfp.finalBidBond,
        resourcesAvailable: rfp.resourcesAvailable,
        licenseAvailable: rfp.licenseAvailable,
        trainingAvailable: rfp.trainingAvailable,
        trainingDetails: rfp.trainingDetails,
        supportAvailable: rfp.supportAvailable,
        supportDescription: rfp.supportDescription,
        supportDuration: rfp.supportDuration,
        summary: rfp.summary
      }));

      const worksheet = xlsx.utils.json_to_sheet(exportData);

      // Adding headers manually to ensure the correct column order
      const headers = [
        ['#', 'Customer Name', 'Deployment Type', 'Product Group', 'RFP Reference Number', 'Inquiry Date', 'Submission Date', 'Project Duration', 'Location', 'Initial Bid Bond', 'Final Bid Bond', 'Resources Available', 'License Available', 'Training Available', 'Training Details', 'Support Available', 'Support Description', 'Support Duration', 'Summary']
      ];
      xlsx.utils.sheet_add_aoa(worksheet, headers, { origin: "A1" });

      const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, "RFPs");
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    saveAs(data, `${fileName}_export_${new Date().getTime()}${EXCEL_EXTENSION}`);
  }

  exportPdf() {
    const doc = new jsPDF('landscape', 'pt', 'a4');

    const columns = [
      { header: '#', dataKey: 'index' },
      { header: 'Customer Name', dataKey: 'customerName' },
      { header: 'Deployment Type', dataKey: 'deploymentType' },
      { header: 'Product Group', dataKey: 'productGroup' },
      { header: 'RFP Reference Number', dataKey: 'rfpReferenceNumber' },
      { header: 'Inquiry Date', dataKey: 'inquiryDate' },
      { header: 'Submission Date', dataKey: 'submissionDate' },
      { header: 'Project Duration', dataKey: 'projectDuration' },
      { header: 'Location', dataKey: 'location' },
      { header: 'Initial Bid Bond', dataKey: 'initialBidBond' },
      { header: 'Final Bid Bond', dataKey: 'finalBidBond' },
      // { header: 'Resources Available', dataKey: 'resourcesAvailable' },
      // { header: 'License Available', dataKey: 'licenseAvailable' },
      // { header: 'Training Available', dataKey: 'trainingAvailable' },
      // { header: 'Training Details', dataKey: 'trainingDetails' },
      // { header: 'Support Available', dataKey: 'supportAvailable' },
      // { header: 'Support Description', dataKey: 'supportDescription' },
      // { header: 'Support Duration', dataKey: 'supportDuration' },
      // { header: 'Summary', dataKey: 'summary' }
    ];

    const data: any[] = (this.table.filteredValue ? this.table.filteredValue : this.table.value).map((rfp: Rfp, index: number) => ({
      index: index + 1,
      customerName: rfp.customerName,
      deploymentType: rfp.deploymentType,
      productGroup: rfp.productGroup,
      rfpReferenceNumber: rfp.rfpReferenceNumber,
      inquiryDate: rfp.inquiryDate ? new Date(rfp.inquiryDate).toLocaleDateString() : '',
      submissionDate: rfp.submissionDate ? new Date(rfp.submissionDate).toLocaleDateString() : '',
      projectDuration: rfp.projectDuration,
      location: rfp.location,
      initialBidBond: rfp.initialBidBond,
      finalBidBond: rfp.finalBidBond,
      // resourcesAvailable: rfp.resourcesAvailable,
      // licenseAvailable: rfp.licenseAvailable,
      // trainingAvailable: rfp.trainingAvailable,
      // trainingDetails: rfp.trainingDetails,
      // supportAvailable: rfp.supportAvailable,
      // supportDescription: rfp.supportDescription,
      // supportDuration: rfp.supportDuration,
      // summary: rfp.summary
    }));

    autoTable(doc, {
      columns: columns,
      body: data,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      columnStyles: {
        index: { halign: 'center' },
        customerName: { halign: 'center' },
        deploymentType: { halign: 'center' },
        productGroup: { halign: 'center' },
        rfpReferenceNumber: { halign: 'center' },
        inquiryDate: { halign: 'center' },
        submissionDate: { halign: 'center' },
        projectDuration: { halign: 'center' },
        location: { halign: 'center' },
        initialBidBond: { halign: 'center' },
        finalBidBond: { halign: 'center' },
        resourcesAvailable: { halign: 'center' },
        licenseAvailable: { halign: 'center' },
        trainingAvailable: { halign: 'center' },
        trainingDetails: { halign: 'center' },
        supportAvailable: { halign: 'center' },
        supportDescription: { halign: 'center' },
        supportDuration: { halign: 'center' },
        summary: { halign: 'center' }
      },
      margin: { top: 20 }
    });

    doc.save('RFPs.pdf');
  }

  showAuditHistory(rfp: Rfp): void {
    this.selectedRfpAuditHistory = rfp.auditHistory;
    this.displayAuditHistoryDialog = true;
  }
}